import { getAppMountUri } from "@dashboard/config";
import * as Sentry from "@sentry/react";
import { createBrowserHistory } from "history";
import type * as React from "react";
import { type RouterProps as BaseRouterProps } from "react-router";
import { Route as BaseRoute, Router as BaseRouter } from "react-router-dom";

type RouterProps = Omit<BaseRouterProps, "history"> & { children: React.ReactNode };

export const history = createBrowserHistory({
  basename: getAppMountUri(),
});

// 창이 처음 연 history 항목을 기억한다 (2026-10-06 KST 추가). 상세 팝업의 뒤로가기가
// 처음 페이지에서는 창을 닫고, 팝업 안에서 옮겨 간 뒤에는 뒤로 가도록 가르는 데 쓴다.
// 첫 페이지가 주소를 replace 로 고치면 키가 바뀌므로 그때는 새 키를 따라간다.
// 모달은 ?action= 을 push 로 열고 replace 로 닫아 같은 페이지 항목이 하나 더 쌓이므로,
// 경로(pathname)가 처음 페이지와 같으면 처음 페이지로 본다 (2026-10-15 KST 수정).
let entryKey = history.location.key;
let entryPathname = history.location.pathname;
let currentKey = history.location.key;

// 이 창에서 지나온 history 항목을 순서대로 기억한다 (2026-11-03 KST 추가).
// 상단 ← · 하단 '뒤로' 가 정해진 상위 주소 대신 직전 페이지로 돌아가는 데 쓴다.
// 새로고침 전 항목이나 다른 창의 항목은 알 수 없으므로, 모르는 항목으로 돌아가면 처음부터 다시 쌓는다.
type VisitedEntry = { key?: string; pathname: string };

let visited: VisitedEntry[] = [{ key: history.location.key, pathname: history.location.pathname }];
let visitedIndex = 0;

history.listen((location, action) => {
  if (action === "REPLACE" && currentKey === entryKey) {
    entryKey = location.key;
    entryPathname = location.pathname;
  }

  currentKey = location.key;

  const entry = { key: location.key, pathname: location.pathname };

  if (action === "PUSH") {
    visited = [...visited.slice(0, visitedIndex + 1), entry];
    visitedIndex = visited.length - 1;
  } else if (action === "REPLACE") {
    visited[visitedIndex] = entry;
  } else {
    const index = visited.findIndex(visitedEntry => visitedEntry.key === location.key);

    if (index === -1) {
      visited = [entry];
      visitedIndex = 0;
    } else {
      visitedIndex = index;
    }
  }
});

export const isAtEntryLocation = (): boolean =>
  history.location.key === entryKey || history.location.pathname === entryPathname;

const normalizePathname = (pathname: string): string => pathname.replace(/\/+$/, "");

// 직전 페이지까지 history 로 몇 칸 돌아가야 하는지 (음수). 돌아갈 곳이 없으면 null (2026-11-03 KST 추가).
// 같은 경로(모달 열고 닫기·탭 전환으로 쌓인 항목)와 추가 화면(/add — 저장하면 상세로 넘어가
// 다시 돌아가면 빈 폼이 열린다)은 건너뛴다.
export const getHistoryBackDelta = (): number | null => {
  const currentPathname = normalizePathname(visited[visitedIndex].pathname);

  for (let index = visitedIndex - 1; index >= 0; index--) {
    const pathname = normalizePathname(visited[index].pathname);

    if (pathname !== currentPathname && !pathname.endsWith("/add")) {
      return index - visitedIndex;
    }
  }

  return null;
};

export const Route = Sentry.withSentryRouting(BaseRoute);

export const Router = (props: RouterProps) => {
  return <BaseRouter history={history}>{props.children}</BaseRouter>;
};
